import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SingleValue } from 'react-select';

import { Table } from '@/common';
import { Spinner } from '@/components/generic';
import { FilterPopUpValues } from '@/components/layouts';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import { PAGE_SIZE } from '@/constants';
import { AuthContext } from '@/contexts';
import { PERMISSION_TYPE } from '@/enums';
import { getDeviceColumns, playerInstallerDownloadUrl } from '@/helpers';
import { FilterDevices } from '@/pages/Devices/FilterDevices';
import { useDeleteDeviceMutation, useGetAllDevices } from '@/services';
import { TestIds } from '@/shared/test-ids';
import { useFilterStore } from '@/store';
import { AllowedPermissions, Device, SelectOptionProps } from '@/types';

export const Devices = () => {
  const [t] = useTranslation('common');
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | undefined>();
  const [isOpenFilterModal, setIsOpenFilterModal] = useState<boolean>(false);
  const [search, setSearch] = useState<string>();
  const { currentUserData: currentUser } = useContext(AuthContext);
  const { organizationFilter } = useFilterStore();
  const navigate = useNavigate();
  const [appliedFiltersCounter, setAppliedFiltersCounter] = useState<number>(0);
  const [oneMinuteAgo, setOneMinuteAgo] = useState<string>();

  const [filters, setFilters] = useState<{
    online: SingleValue<SelectOptionProps>;
    status: SingleValue<SelectOptionProps>;
    organization: SingleValue<SelectOptionProps>;
  }>({
    online: null,
    status: null,
    organization: null
  });

  const getOrganizationId = () => {
    if (organizationFilter && organizationFilter.value) {
      return organizationFilter.value as number;
    } else if (filters.organization === null && currentUser?.organizations.length === 1) {
      return currentUser?.organizations[0].id as number;
    } else {
      return filters.organization?.value as number;
    }
  };

  const {
    isLoading,
    data: allDevices,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  } = useGetAllDevices(
    PAGE_SIZE,
    search,
    filters.online?.value as boolean,
    filters.online?.value === null ? undefined : (oneMinuteAgo as string),
    filters.status?.value as boolean,
    getOrganizationId()
  );

  const searchCallback = (query: string) => {
    setSearch(query);
  };

  const permissions: AllowedPermissions = {
    createPermission: PERMISSION_TYPE.CREATE_DEVICE,
    editPermission: PERMISSION_TYPE.UPDATE_DEVICE,
    deletePermission: PERMISSION_TYPE.DELETE_DEVICE,
    viewPermission: PERMISSION_TYPE.VIEW_DEVICES
  };

  const { mutate: deleteDevice } = useDeleteDeviceMutation(queryClient, {
    onSuccess: () => {
      setIsOpenDeleteModal(false);
      navigate('/devices');
    },
    onError: () => {
      setIsOpenDeleteModal(false);
    }
  });

  const handleCreateDevice = () => {
    navigate('/devices/create');
  };

  const handleEditDevice = (device: Device) => {
    navigate(`/devices/${device.id}/edit`);
  };

  const handleDelete = (device: Device) => {
    setSelectedDevice(device);
    setIsOpenDeleteModal(true);
  };

  const handleViewDetails = (row: Device) => {
    navigate('/devices/' + row.id);
  };

  const onCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
    setSelectedDevice(undefined);
  };

  const handleFilterModalVisibility = () => {
    setIsOpenFilterModal(!isOpenFilterModal);
  };
  const resetFilters = () => {
    setIsOpenFilterModal(false);
    setFilters({ online: null, status: null, organization: null });
    setAppliedFiltersCounter(0);
  };

  const applyFilters = (values: {
    filterByOnlineStatus: SelectOptionProps;
    filterByEnabledStatus: SelectOptionProps;
    filterByOrganization: SelectOptionProps;
  }) => {
    setIsOpenFilterModal(false);
    setFilters({
      online: values.filterByOnlineStatus,
      status: values.filterByEnabledStatus,
      organization: values.filterByOrganization
    });
    setOneMinuteAgo(new Date(new Date().getTime() - 1 * 60 * 1000).toISOString());
  };

  useEffect(() => {
    const numberOfFilters = Object.values(filters).filter((value) => value !== null);
    setAppliedFiltersCounter(numberOfFilters.length);
  }, [filters]);

  return (
    <div className='flex size-full flex-col items-center'>
      {isLoading ? (
        <Spinner />
      ) : (
        <div className='flex h-screen w-full flex-col px-3 pb-3'>
          {allDevices && (
            <Table
              data={allDevices.pages.flatMap((page) => page.data?.items)}
              columns={getDeviceColumns()}
              testIdPrefix={TestIds.devices.prefix}
              tableTitle={t('devices')}
              addButtonLabel={t('addDevice')}
              downloadButtonLabel={t('downloadPlayer')}
              downloadUrl={playerInstallerDownloadUrl()}
              search={searchCallback}
              handleCreate={handleCreateDevice}
              handleEdit={(item) => handleEditDevice(item as Device)}
              handleDelete={(item) => handleDelete(item as Device)}
              handleViewDetails={(item) => handleViewDetails(item as Device)}
              permissions={permissions}
              handleOpenFilterModal={handleFilterModalVisibility}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              widerLayout
              numberOfAppliedFilters={appliedFiltersCounter}
              hiddenColumnsMobile={['status']}
            />
          )}
        </div>
      )}
      {selectedDevice && (
        <ConfirmationModal
          isOpen={isOpenDeleteModal}
          message={t('deleteMessage', { item: selectedDevice.name })}
          onClose={onCloseDeleteModal}
          onConfirm={() => deleteDevice(selectedDevice?.id)}
        />
      )}
      {isOpenFilterModal && (
        <FilterDevices
          apply={applyFilters as (values: FilterPopUpValues) => void}
          reset={resetFilters}
          onClose={() => {
            setIsOpenFilterModal(false);
          }}
          filterByOnlineStatus={filters.online}
          filterByEnabledStatus={filters.status}
          filterByOrganization={filters.organization}
        />
      )}
    </div>
  );
};
